import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { sendOutboundMessage, type SendOutboundError } from '../api/messages';
import { getApprovedTemplates, type ApprovedTemplate } from '../api/templates';
import { useAuth } from '../context/AuthContext';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { useSnackbar } from './Snackbar';
import { ownershipNotice } from '../lib/ownershipNotice';
import type { Branch } from '../api/branches';

const COMPANY_NAME_KEY = 'outbound_company_name';

const baseCls =
  'w-full bg-surface-container-high border border-transparent rounded-corner-sm px-3 text-body-medium ' +
  'text-on-surface placeholder:text-on-surface-variant ' +
  'focus:outline-none focus:border-outline ' +
  'transition-colors duration-m3-short ease-m3-standard';

const inputCls = baseCls + ' h-11';
const areaCls  = baseCls + ' py-3 resize-none';

const labelCls = 'block text-label-medium text-on-surface-variant mb-1.5';

export function OutboundModal({
  branches,
  onClose,
  onSuccess,
  initialPhone,
  lockPhone = false,
  initialBranchId,
  initialMode = 'text',
  initialTemplateName,
  initialTemplateLanguage,
  lockToTemplate = false,
}: {
  branches: Branch[];
  onClose: () => void;
  onSuccess: (ticketId: string) => void;
  /** Prefill + lock the recipient (e.g. reused for a ticket's closed-window template). */
  initialPhone?: string;
  lockPhone?: boolean;
  initialBranchId?: string;
  initialMode?: 'text' | 'template';
  /** Safe retry prefill: selects the approved template, but parameter values
   * remain blank because the legacy stored comma-separated tail is lossy. */
  initialTemplateName?: string;
  /** The retried template's language, when the failed row recorded it (a name
   *  can exist in several languages; never another language of it). */
  initialTemplateLanguage?: string;
  /** Lock the modal to template mode: forces template, hides the Text/Template
   *  toggle and the free-text box (used by the Inbox "+" new-conversation flow).
   *  When false (default) behavior is unchanged — the toggle + Text box render. */
  lockToTemplate?: boolean;
}) {
  const { t } = useTranslation();
  const { show: showSnackbar } = useSnackbar();
  const { agent } = useAuth();

  // ─── Form state ────────────────────────────────────────────────────────────
  const [branchId, setBranchId] = useState<string>(() =>
    initialBranchId || (branches.length === 1 && branches[0] ? branches[0].id : ''),
  );
  const [phone, setPhone] = useState(initialPhone ?? '');
  const [mode, setMode] = useState<'text' | 'template'>(lockToTemplate ? 'template' : initialMode);
  const [text, setText] = useState('');

  // Template state
  const [templates, setTemplates] = useState<ApprovedTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateName, setTemplateName] = useState('');
  // A template is a name AND a language: the same name can exist in several.
  const [templateLanguage, setTemplateLanguage] = useState('');
  const [params, setParams] = useState<string[]>([]);

  const [loading, setLoading] = useState(false);
  // React state is not synchronous: without a ref, two submit events can both
  // observe loading=false before the disabled state renders. The stable
  // idempotency key protects network replays; this guard also closes the local
  // double-submit race before React paints the disabled state.
  const submittingRef = useRef(false);
  const clientMessageIdRef = useRef(
    (() => {
      try { return crypto.randomUUID(); }
      catch { return `outbound-${Date.now()}-${Math.random().toString(36).slice(2)}`; }
    })(),
  );
  const [error, setError] = useState<string | null>(null);
  // Ownership conflict is shown in a sticky banner above the footer (always
  // visible) and blocks Send until the phone number changes.
  const [conflict, setConflict] = useState<string | null>(null);

  const phoneRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Modal focus trap + Esc + focus-return (§11). Declared BEFORE the phone
  // auto-focus below so its effect runs first and captures the opener (still
  // outside the dialog) as the invoker; if it ran after, it would capture the
  // phone field and drop focus to <body> on close instead of returning to the
  // control that opened the modal. Its own rAF then sees focus is already
  // inside (on the phone) and leaves it there.
  useFocusTrap(true, dialogRef, onClose);

  // Focus the phone field on mount (unless it's prefilled + locked).
  useEffect(() => { if (!lockPhone) phoneRef.current?.focus(); }, [lockPhone]);

  // Load approved templates when switching to template mode.
  useEffect(() => {
    if (mode !== 'template') return;
    setTemplatesLoading(true);
    getApprovedTemplates()
      .then((list) => {
        setTemplates(list);
        if (list.length > 0 && list[0]) {
          const selected = list.find((template) => template.name === initialTemplateName
            && (initialTemplateLanguage === undefined || template.language === initialTemplateLanguage)) ?? list[0];
          setTemplateName(selected.name);
          setTemplateLanguage(selected.language);
          const count = selected.paramCount;
          const initial = Array.from({ length: count }, (_, i) =>
            initialTemplateName ? '' : (i === 0 ? (localStorage.getItem(COMPANY_NAME_KEY) ?? '') : ''),
          );
          setParams(initial);
        }
      })
      .catch(() => { setTemplates([]); })
      .finally(() => { setTemplatesLoading(false); });
  }, [initialTemplateLanguage, initialTemplateName, mode]);

  // When template selection changes, resize params array.
  function selectTemplate(key: string) {
    const sep = key.lastIndexOf('|');
    const name = key.slice(0, sep);
    const language = key.slice(sep + 1);
    setTemplateName(name);
    setTemplateLanguage(language);
    const tmpl = templates.find((t) => t.name === name && t.language === language);
    if (!tmpl) return;
    setParams(
      Array.from({ length: tmpl.paramCount }, (_, i) =>
        i === 0 ? (localStorage.getItem(COMPANY_NAME_KEY) ?? '') : '',
      ),
    );
  }

  const selectedTmpl = templates.find((t) => t.name === templateName && t.language === templateLanguage) ?? null;

  const preview = selectedTmpl?.body
    ? selectedTmpl.body.replace(
        /\{\{(\d+)\}\}/g,
        (_, n) => params[parseInt(n, 10) - 1] ?? `{{${n}}}`,
      )
    : '';

  // ─── Submit ─────────────────────────────────────────────────────────────────
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setError(null);
    setLoading(true);
    try {
      const { ticketId, ownership } = await sendOutboundMessage({
        branchId,
        phone,
        mode,
        text: mode === 'text' ? text : undefined,
        templateName: mode === 'template' ? templateName : undefined,
        templateLanguage: mode === 'template' && templateLanguage ? templateLanguage : undefined,
        // 0-param templates: omit params entirely rather than sending [].
        params: mode === 'template' && params.length > 0 ? params : undefined,
        clientMessageId: clientMessageIdRef.current,
      });
      if (mode === 'template' && params[0]) {
        localStorage.setItem(COMPANY_NAME_KEY, params[0]);
      }
      // The send succeeded; say what happened to ownership separately, so an
      // out-of-scope or superseded outcome is never mistaken for a takeover.
      const notice = ownershipNotice(t, ownership);
      if (notice) showSnackbar({ message: notice.message });
      onSuccess(ticketId);
    } catch (err) {
      const e = err as SendOutboundError;
      if (e.error === 'invalid_phone') {
        setError(t('outbound.error_invalid_phone'));
      } else if (e.error === 'outside_window') {
        setError(t('outbound.error_outside_window'));
      } else if (e.error === 'no_number') {
        setError(t('outbound.error_no_number'));
      } else if (e.error === 'assigned_to_other') {
        // Prominent, sticky, blocks Send until the number changes.
        setConflict(t('outbound.error_assigned_to_other', { name: e.agentName ?? '' }));
      } else if (e.error === 'template_not_available') {
        setError(t('outbound.error_template_not_available'));
      } else {
        setError(t('outbound.error_send_failed'));
      }
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  }

  const allParamsFilled = params.every((p) => p.trim() !== '');
  const canSend =
    branchId !== '' &&
    phone.trim() !== '' &&
    (mode === 'text'
      ? text.trim() !== ''
      : templateName !== '' && allParamsFilled && templates.length > 0);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
    >
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        className="absolute inset-0"
        style={{ backgroundColor: 'var(--bg-overlay)' }}
        onClick={onClose}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('outbound.modal_title')}
        className="relative bg-surface rounded-corner-xl shadow-elevation-3 border border-outline-variant
          w-full max-w-md mx-3 min-[400px]:mx-4 dialog-max-h flex flex-col overflow-hidden"
      >
        {/* Header — pinned (non-scrolling flex row) */}
        <div className="shrink-0 flex items-center justify-between px-5 py-4 bg-surface
          border-b border-outline-variant/60 [@media(max-height:300px)]:py-0 [:root[data-vv-short]_&]:py-0">
          <h3 className="text-title-medium text-on-surface">{t('outbound.modal_title')}</h3>
          <button
            onClick={onClose}
            className="flex items-center justify-center size-11 -me-2 bp-md:me-0 bp-md:size-8 rounded-corner-full text-xl leading-none
              text-on-surface-variant hover:bg-on-surface/[0.08]
              transition-colors duration-m3-short ease-m3-standard
              focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
            aria-label={t('cancel')}
          >
            ×
          </button>
        </div>

        {/* Form — flex column: scrollable body + pinned footer. onSubmit unchanged. */}
        <form onSubmit={(e) => void handleSubmit(e)} className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* Scrollable body — only this area scrolls */}
          <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 flex flex-col gap-4 [@media(max-height:300px)]:py-2 [:root[data-vv-short]_&]:py-2">
          {/* Branch selector — hidden when the agent only has one branch */}
          {branches.length > 1 && (
            <div>
              <label className={labelCls}>
                {t('outbound.field_branch')}
              </label>
              <select
                required
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
                className={inputCls}
              >
                <option value="">{t('outbound.field_branch_placeholder')}</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Phone */}
          <div>
            <label className={labelCls}>
              {t('outbound.field_phone')}
            </label>
            <input
              ref={phoneRef}
              type="tel"
              dir="ltr"
              required
              readOnly={lockPhone}
              value={phone}
              onChange={(e) => { setPhone(e.target.value); setConflict(null); }}
              placeholder={t('outbound.field_phone_hint')}
              className={`${inputCls}${lockPhone ? ' opacity-70 cursor-not-allowed' : ''}`}
            />
          </div>

          {/* Mode toggle — hidden when locked to template (Inbox "+" flow) */}
          {!lockToTemplate && (
            <div>
              <label className={labelCls}>
                {t('outbound.field_mode')}
              </label>
              <div className="flex items-center gap-0.5 bg-surface-container-highest rounded-corner-sm p-1 w-fit">
                {(['text', 'template'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMode(m)}
                    className={`text-label-medium px-3 py-1.5 min-h-11 bp-md:min-h-0 rounded-corner-sm
                      transition-all duration-m3-short ease-m3-standard ${
                      mode === m
                        ? 'bg-surface text-on-surface shadow-sm'
                        : 'text-on-surface-variant hover:text-on-surface'
                    }`}
                  >
                    {t(`outbound.mode_${m}`)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Text mode — never rendered when locked to template */}
          {!lockToTemplate && mode === 'text' && (
            <div>
              <label className={labelCls}>
                {t('outbound.field_text')}
              </label>
              <textarea
                required
                rows={4}
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={4096}
                placeholder={t('outbound.field_text_placeholder')}
                className={areaCls}
              />
            </div>
          )}

          {/* Template mode */}
          {mode === 'template' && (
            <>
              {templatesLoading ? (
                <p className="text-body-medium text-on-surface-variant">{t('outbound.loading_templates')}</p>
              ) : templates.length === 0 ? (
                <div className="rounded-corner-md border border-outline-variant bg-surface-container-highest px-4 py-4 text-center">
                  <p className="text-body-medium text-on-surface mb-1">{t('outbound.no_approved_templates')}</p>
                  <p className="text-body-small text-on-surface-variant">
                    {agent?.role === 'admin'
                      ? t('outbound.no_approved_templates_hint_admin')
                      : t('outbound.no_approved_templates_hint')}
                  </p>
                </div>
              ) : (
                <>
                  {/* Template selector */}
                  <div>
                    <label className={labelCls}>
                      {t('outbound.field_template')}
                    </label>
                    <select
                      required
                      aria-label={t('outbound.field_template')}
                      value={`${templateName}|${templateLanguage}`}
                      onChange={(e) => selectTemplate(e.target.value)}
                      className={inputCls}
                    >
                      {templates.map((tpl) => (
                        <option key={`${tpl.name}|${tpl.language}`} value={`${tpl.name}|${tpl.language}`}>
                          {templates.some((other) => other !== tpl && other.name === tpl.name)
                            ? `${tpl.name} (${tpl.language})`
                            : tpl.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Per-param inputs */}
                  {params.map((val, i) => (
                    <div key={i}>
                      <label className={labelCls}>
                        {t('outbound.param_label', { n: i + 1 })}
                      </label>
                      <input
                        type="text"
                        required
                        aria-label={t('outbound.param_label', { n: i + 1 })}
                        value={val}
                        onChange={(e) => {
                          const next = [...params];
                          next[i] = e.target.value;
                          setParams(next);
                        }}
                        className={inputCls}
                      />
                    </div>
                  ))}

                  {/* Live preview */}
                  {preview && (
                    <div className="rounded-corner-md border border-outline-variant bg-surface-container-high px-3 py-3">
                      <p className="text-label-small text-on-surface-variant mb-1">{t('outbound.preview_label')}</p>
                      <p className="text-body-medium text-on-surface whitespace-pre-wrap" dir="auto">{preview}</p>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* Error banner — stays in the scrollable body with the fields */}
          {error && (
            <div className="text-body-small rounded-corner-sm px-3 py-2 bg-error-container text-on-error-container">
              {error}
            </div>
          )}
          </div>
          {/* end scrollable body */}

          {/* Ownership conflict — sticky above the footer, ALWAYS visible
              regardless of how far the template preview scrolls. */}
          {conflict && (
            <div
              role="alert"
              data-testid="outbound-conflict"
              className="shrink-0 text-body-small px-5 py-3 bg-error-container text-on-error-container
                border-t border-outline-variant/60"
            >
              {conflict}
            </div>
          )}

          {/* Actions — pinned footer; still inside <form> so Send submits */}
          <div className="shrink-0 flex gap-2 justify-end px-5 py-4 border-t border-outline-variant/60 bg-surface [@media(max-height:300px)]:py-2 [:root[data-vv-short]_&]:py-2">
            <button
              type="button"
              onClick={onClose}
              className="text-label-large text-on-surface-variant hover:text-on-surface hover:bg-on-surface/[0.08]
                px-4 h-11 bp-md:h-10 rounded-corner-full transition-colors duration-m3-short ease-m3-standard
                focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || !canSend || conflict !== null}
              className="bg-primary text-on-primary disabled:opacity-50
                text-label-large rounded-corner-full px-5 h-11 bp-md:h-10
                transition-all duration-m3-short ease-m3-standard
                enabled:hover:-translate-y-px
                focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
            >
              {loading ? '...' : t('outbound.send')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
